import React, { useState, useEffect, useCallback } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import { useCompany, PLAN_LABELS, PLAN_COLORS, ROLE_LABELS, ROLE_COLORS } from './CompanyContext';
import type { Company } from './CompanyContext';
import logoUrl from './assets/storsafe-logo.png';
import { useImpersonation } from './ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from './lib/pricing';
import UpgradeWall from './components/UpgradeWall';
import type { CompanyWithStats } from './SuperAdmin';
import Products from './Products';
import CompatibilityMatrix from './CompatibilityMatrix';
import Warehouses from './Warehouses';
import Users from './Users';
import SuperAdmin from './SuperAdmin';
import Reports from './Reports';
import Emergency from './Emergency';
import Audit from './Audit';
import LayoutPro from './LayoutPro';
import Terms, { TERMS_VERSION } from './Terms';
import Settings from './Settings';
import OnboardingModal from './OnboardingModal';
import OnboardingChecklist from './components/onboarding/OnboardingChecklist';
import PpxButton from './components/payments/PpxButton';
import { buildPagoPluxData, handlePagoPluxAuthorize, PagoPluxAuthorizeResponse } from './lib/pagoplux';
import { calculatePrice } from './lib/pricing';
import OnboardingTour from './components/onboarding/OnboardingTour';
import { useMediaQuery, CONSULTA_MOVIL } from './lib/useMediaQuery';
import SSOProfilePage from './pages/SSOProfilePage';
import ChecklistPage from './pages/ChecklistPage';
import WorkersPage from './pages/WorkersPage';
import SedesPage from './pages/SedesPage';
import IncidentsPage from './pages/IncidentsPage';
import SSODashboardPage from './pages/SSODashboardPage';
import EPPPage from './pages/EPPPage';
import SSOCalendarPage from './pages/SSOCalendarPage';
import SSODocumentsPage from './pages/SSODocumentsPage';
import TrainingsPage from './pages/TrainingsPage';
import LearningAdminPage from './pages/LearningAdminPage';
import SGALabelsPage from './pages/SGALabelsPage';
import InspectionsPage from './pages/InspectionsPage';
import IperPage from './pages/IperPage';
import HealthSurveillancePage from './pages/HealthSurveillancePage';
import WorkPermitsPage from './pages/WorkPermitsPage';
import JobPositionsPage from './pages/JobPositionsPage';
import EmergencyPlanPage from './pages/EmergencyPlanPage';
import PlanEmergenciasV2 from './pages/pe/PlanEmergenciasV2';
import InspeccionesV2 from './pages/inspecciones/InspeccionesV2';
import InventarioActivos from './pages/mantenimiento/InventarioActivos';
import { qrDeLaCarga } from './lib/mantenimiento/mant-qr';
import PsychosocialRiskPage from './pages/PsychosocialRiskPage';
import GestionTecnicaConstructorPage from './pages/GestionTecnicaConstructorPage';
import CoshhMethodologyPage from './pages/CoshhMethodologyPage';
import BiogavalMethodologyPage from './pages/BiogavalMethodologyPage';
import IluminacionMethodologyPage from './pages/IluminacionMethodologyPage';
import RuidoMethodologyPage from './pages/RuidoMethodologyPage';
import EstresTermicoMethodologyPage from './pages/EstresTermicoMethodologyPage';
import RulaMethodologyPage from './pages/RulaMethodologyPage';
import RebaMethodologyPage from './pages/RebaMethodologyPage';
import NioshMethodologyPage from './pages/NioshMethodologyPage';
import SnookCirielloMethodologyPage from './pages/SnookCirielloMethodologyPage';
import RosaMethodologyPage from './pages/RosaMethodologyPage';
import PsicosocialCodigosPage from './pages/PsicosocialCodigosPage';
import PsicosocialResultadosPage from './pages/PsicosocialResultadosPage';
import { useComplianceScore } from './lib/complianceScore';
import { CLAVE_SECCION, debeVolverAlDashboard, seccionInicial } from './lib/visibilidad-modulos';
import { nivelGrupo } from './lib/compatibilidad';
import {
  MENU_BASE, construirMenu, entradasItemsAnexo, filtrarMenu, itemDeNav, idItemAnexo,
  type EntradaMenu, type NavItem,
} from './lib/menu';
import { useItemsAnexo } from './lib/useItemsAnexo';
import ItemAnexoPage from './pages/ItemAnexoPage';
import { puedeVerChecklist } from './lib/checklist-reglas';
import './Dashboard.css';

// NavItem y el menú (secciones, visibilidad) viven en src/lib/menu.ts (bloque 7).

interface Props {
  user: User;
}

interface StatTemplate {
  label:    string;
  key:      'products' | 'warehouses' | 'alerts' | 'users';
  tab:      NavItem;
  limitKey: keyof Company | null;
  icon:     React.ReactNode;
  color:    string;
  bg:       string;
}

const STAT_TEMPLATES: StatTemplate[] = [
  { label: 'Total Productos',             key: 'products',   tab: 'productos',      limitKey: 'max_products',   icon: <BoxIcon />,       color: '#22c55e', bg: 'rgba(34,197,94,0.1)'   },
  { label: 'Bodegas Activas',             key: 'warehouses', tab: 'bodegas',        limitKey: 'max_warehouses', icon: <WarehouseIcon />, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)'  },
  { label: 'Alertas de Incompatibilidad', key: 'alerts',      tab: 'compatibilidad', limitKey: null,             icon: <AlertIcon />,     color: '#f59e0b', bg: 'rgba(245,158,11,0.1)'  },
  { label: 'Usuarios Activos',            key: 'users',       tab: 'usuarios',       limitKey: 'max_users',      icon: <UsersIcon />,     color: '#a78bfa', bg: 'rgba(167,139,250,0.1)' },
];

// ── Compatibilidad: tabla única (src/lib/compatibilidad.ts) ─────────────────
// Una zona tiene alerta si algún par no es compatible (precaución o incompatible).
function hasZoneAlert(productClasses: string[][]): boolean {
  return nivelGrupo(productClasses.map(sga_classes => ({ sga_classes }))) !== 'compatible';
}

// Ícono de cada entrada del menú (los datos están en src/lib/menu.ts).
const ICONOS_MENU: Partial<Record<NavItem, React.ReactNode>> = {
  'dashboard': <NavDashboardIcon />,
  'dashboard-sso': <NavChartBarIcon />,
  'calendario-sso': <NavCalendarIcon />,
  'documentos-sso': <NavDocsIcon />,
  'checklist': <NavSSOIcon />,
  'sso-perfil': <NavSSOIcon />,
  'iper': <NavIperIcon />,
  'sedes': <NavWarehouseIcon />,
  'puestos-trabajo': <NavBriefcaseIcon />,
  'constructor-gestion-tecnica': <NavIperIcon />,
  'permisos-trabajo': <NavPermitIcon />,
  'productos': <NavBoxIcon />,
  'compatibilidad': <NavGridIcon />,
  'bodegas': <NavWarehouseIcon />,
  'emergencias': <NavAlertIcon />,
  'etiquetado-sga': <NavLabelIcon />,
  'layout': <NavLayoutIcon />,
  'metodologia-coshh': <NavGridIcon />,
  'higiene-fisico': <NavIperIcon />,
  'metodologia-iluminacion': <NavGridIcon />,
  'metodologia-ruido': <NavGridIcon />,
  'metodologia-estres-termico': <NavGridIcon />,
  'higiene-biologico': <NavIperIcon />,
  'metodologia-biogaval': <NavGridIcon />,
  'metodologia-rula': <NavGridIcon />,
  'metodologia-reba': <NavGridIcon />,
  'ergonomia-manipulacion-cargas': <NavIperIcon />,
  'metodologia-snook-ciriello': <NavIperIcon />,
  'ergonomia-repetitividad': <NavIperIcon />,
  'ergonomia-puestos-oficina': <NavIperIcon />,
  'riesgo-psicosocial': <NavHealthIcon />,
  'cuestionario-psicosocial': <NavHealthIcon />,
  'resultados-psicosocial': <NavHealthIcon />,
  'capacitaciones': <NavTrainingIcon />,
  'learning': <NavTrainingIcon />,
  'trabajadores': <NavWorkersIcon />,
  'inspecciones': <NavInspectIcon />,
  'inspecciones-v2': <NavInspectIcon />,
  'inventario-activos': <NavBriefcaseIcon />,
  'vigilancia-salud': <NavHealthIcon />,
  'plan-emergencias': <NavAlertIcon />,
  'plan-emergencias-v2': <NavAlertIcon />,
  'incidentes': <NavIncidentsIcon />,
  'epp': <NavShieldCheckIcon />,
  'usuarios': <NavUsersIcon />,
  'reportes': <NavReportIcon />,
  'auditoria': <NavAuditIcon />,
  'configuracion': <NavSettingsIcon />,
  'terminos': <NavTermsIcon />,
  'superadmin': <NavShieldIcon />,
};

type EntradaConIcono = EntradaMenu & { icon: React.ReactNode };

const conIcono = (e: EntradaMenu): EntradaConIcono => ({ ...e, icon: ICONOS_MENU[e.id] ?? <NavDocsIcon /> });

const ALL_NAV_ITEMS: EntradaConIcono[] = MENU_BASE.map(conIcono);

export default function Dashboard({ user }: Props) {
  const { company, profile, loading: cargandoPerfil, refresh } = useCompany();
  const { impersonated, impersonate, exitImpersonation } = useImpersonation();
  const [active, setActive]           = useState<NavItem>(() => {
    const stored = sessionStorage.getItem('ss_init_tab');
    if (stored) sessionStorage.removeItem('ss_init_tab');
    // Mantenimiento M1: enlace de QR (?activo=<id>), ya con sesión: abre el inventario y la ficha.
    if (qrDeLaCarga()) return 'inventario-activos';
    // Lote A: al recargar se vuelve a la sección en la que estaba (antes siempre volvía al Dashboard).
    return seccionInicial(stored, sessionStorage.getItem(CLAVE_SECCION),
      id => !!itemDeNav(id) || ALL_NAV_ITEMS.some(i => i.id === id && !i.superadminOnly)) as NavItem;
  });
  useEffect(() => { try { sessionStorage.setItem(CLAVE_SECCION, active); } catch {} }, [active]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Celular (< 768 px): el menú empieza cerrado y se abre como panel encima
  // del contenido con ☰; en escritorio sigue fijo y colapsable (§17 ítem 86).
  const esMovil = useMediaQuery(CONSULTA_MOVIL);
  const [menuMovilAbierto, setMenuMovilAbierto] = useState(false);
  const menuExpandido = esMovil || sidebarOpen;
  useEffect(() => { if (!esMovil) setMenuMovilAbierto(false); }, [esMovil]);
  useEffect(() => {
    if (!menuMovilAbierto) return;
    const alTeclear = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuMovilAbierto(false); };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [menuMovilAbierto]);
  const [lockedFeature, setLockedFeature] = useState<NavItem | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('ss_sidebar_state');
      if (!stored) return new Set();
      const parsed = JSON.parse(stored) as Record<string, boolean>;
      return new Set(Object.keys(parsed).filter((section) => parsed[section]));
    } catch {
      return new Set();
    }
  });
  const [expandedSubsections, setExpandedSubsections] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('ss_sidebar_subsections');
      if (!stored) return new Set(['Gestión del Riesgo Químico']);
      const parsed = JSON.parse(stored) as Record<string, boolean>;
      return new Set(Object.keys(parsed).filter((subsection) => parsed[subsection]));
    } catch {
      return new Set(['Gestión del Riesgo Químico']);
    }
  });
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('ss_sidebar_groups');
      if (!stored) return new Set(['Higiene Industrial']);
      const parsed = JSON.parse(stored) as Record<string, boolean>;
      return new Set(Object.keys(parsed).filter((group) => parsed[group]));
    } catch {
      return new Set(['Higiene Industrial']);
    }
  });
  const [termsChecked, setTermsChecked]             = useState(false);
  const [emergencyConsentChecked, setEmergencyConsent] = useState(true);
  const [termsAccepting, setTermsAccepting]         = useState(false);
  const [showFullTerms, setShowFullTerms]            = useState(false);

  // Welcome banner after a successful payment redirect (?welcome=true)
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('welcome') !== 'true') return;
    setShowWelcome(true);
    params.delete('welcome');
    const newSearch = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    const timer = setTimeout(() => setShowWelcome(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Trial extension request state
  const [extRequest, setExtRequest]     = useState<{ id: string; status: string } | null | undefined>(undefined); // undefined = loading
  const [extMessage, setExtMessage]     = useState('');
  const [ppxData,    setPpxData]    = useState<Record<string, any> | null>(null);
  const [ppxLoading, setPpxLoading] = useState(false);
  const [ppxError,   setPpxError]   = useState('');
  const [selectedPlan, setSelectedPlan] = useState<'starter' | 'professional' | 'enterprise'>('professional');
  const [selectedCycle, setSelectedCycle] = useState<'mensual' | 'anual'>('anual');
  const [extSubmitting, setExtSubmitting] = useState(false);
  const [showSupportEmail, setShowSupportEmail] = useState(false);
  const [emailCopied, setEmailCopied]   = useState(false);
  const showTermsModal = profile && !impersonated && (!profile.terms_accepted_at || profile.terms_accepted_version !== TERMS_VERSION);

  // Superadmin siempre ve solo su panel (salvo cuando está impersonando una empresa)
  const isSuperAdmin = profile?.role === 'superadmin';
  useEffect(() => {
    if (isSuperAdmin && !impersonated && active === 'dashboard') setActive('superadmin');
  }, [isSuperAdmin, impersonated, active]);

  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);
  const [counts, setCounts] = useState({ products: 0, warehouses: 0, alerts: 0, users: 0 });
  const [pendingSds, setPendingSds] = useState<{ id: string; name: string }[]>([]);
  const [sdsPanelOpen, setSdsPanelOpen] = useState(false);

  // The company shown in UI: impersonated one or own company
  const displayCompany = impersonated ?? company;

  // Módulos SSO/Cumplimiento: visibles solo para empresas marcadas como "dev"
  // (la propia empresa de prueba/demo del superadmin), no por rol.
  const isDevCompany = displayCompany?.is_dev === true;
  // El trial de 24h ya NO destapa módulos ocultos — un prospecto en trial ve
  // exactamente lo mismo que un cliente pagado sin trial (hoy: Química).
  // Decisión 2026-09-17: se eliminó hasActiveModuleTrial (leía
  // la columna companies.trial_hidden_modules_until, eliminada el 26/09/2026)
  // porque dejó de tener uso real —
  // solo EMPRESA TEST (is_dev=true) debe ver los módulos ocultos. Si en el
  // futuro se necesita un trial que sí destape módulos puntuales, no reactivar
  // esta lógica binaria: diseñar un mecanismo que distinga módulos "liberados
  // para venta" de módulos "en desarrollo", ya que hoy ALL_NAV_ITEMS no
  // distingue entre esos dos casos dentro de hiddenForClients.
  const canSeeHiddenModules = isDevCompany;
  // Resultados Psicosocial: solo admin/superadmin (superadmin impersonando conserva
  // profile.role === 'superadmin'). Guard de ruta además del minRole del menú.
  const canSeePsicosocialResultados = canSeeHiddenModules && (isSuperAdmin || profile?.role === 'admin');
  // Bloque 9 (M19): el Checklist no es para el viewer (también si llega por "Ver en el Checklist").
  const canSeeChecklist = canSeeHiddenModules && puedeVerChecklist(profile?.role, !!impersonated);
  useEffect(() => {
    const activeItem = ALL_NAV_ITEMS.find((item) => item.id === active);
    // Las páginas de ítem del Anexo 1 (bloque 7) tienen la visibilidad de Documentos SSO. Lote A: mientras el perfil y la
    // empresa cargan no se decide (al recargar o con un enlace directo, EMPRESA TEST volvía al Dashboard).
    if (debeVolverAlDashboard({
      cargando: cargandoPerfil, oculto: !!activeItem?.hiddenForClients || !!itemDeNav(active),
      esResultadosPsicosocial: active === 'resultados-psicosocial', esChecklist: active === 'checklist', hayPerfil: !!profile,
      veOcultos: canSeeHiddenModules, veResultadosPsicosocial: canSeePsicosocialResultados, veChecklist: canSeeChecklist,
    })) setActive('dashboard');
  }, [active, cargandoPerfil, canSeeHiddenModules, canSeePsicosocialResultados, canSeeChecklist, profile]);

  const complianceScore = useComplianceScore(displayCompany?.id);

  // Trial calculations (after displayCompany is resolved)
  const trialDaysLeft = (() => {
    if (!displayCompany?.trial_ends_at || displayCompany.plan !== 'trial') return null;
    const diffMs = new Date(displayCompany.trial_ends_at).getTime() - Date.now();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  })();
  const hasActiveSubscription =
    displayCompany?.subscription_status === 'active' &&
    !!displayCompany?.current_period_end &&
    new Date(displayCompany.current_period_end).getTime() > Date.now();
  const isTrialExpired = trialDaysLeft !== null && trialDaysLeft <= 0 && !hasActiveSubscription;
  const isTrialUrgent  = trialDaysLeft !== null && trialDaysLeft > 0 && trialDaysLeft <= 3;

  // Dismissible trial banner (top of dashboard) — only while subscription_status='trial'
  // and the trial hasn't expired yet (expired trial uses the blocking overlay instead).
  const trialBannerDaysLeft = (() => {
    if (displayCompany?.subscription_status !== 'trial' || !displayCompany?.trial_ends_at) return null;
    const diffMs = new Date(displayCompany.trial_ends_at).getTime() - Date.now();
    if (diffMs <= 0) return null;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  })();
  const trialBannerVariant: 'info' | 'warning' | 'danger' =
    trialBannerDaysLeft !== null && trialBannerDaysLeft <= 0 ? 'danger'
    : trialBannerDaysLeft !== null && trialBannerDaysLeft <= 2 ? 'warning'
    : 'info';
  const trialBannerText =
    trialBannerDaysLeft !== null && trialBannerDaysLeft <= 0
      ? 'Tu prueba vence hoy.'
      : trialBannerDaysLeft !== null && trialBannerDaysLeft <= 2
        ? `Tu prueba vence en ${trialBannerDaysLeft} ${trialBannerDaysLeft === 1 ? 'día' : 'días'}. ¡Contrata ahora!`
        : `Tu período de prueba vence en ${trialBannerDaysLeft} días.`;
  const showTrialBanner = trialBannerDaysLeft !== null && !isSuperAdmin && !trialBannerDismissed;

  // Apartados por ítem del Anexo 1 (bloque 7, B y C): los GA y SP que aplican,
  // según el Perfil SSO. Se recalculan al salir del Perfil SSO.
  const itemsAnexo = useItemsAnexo(displayCompany?.id, canSeeHiddenModules, active);
  const menuCompleto: EntradaConIcono[] = React.useMemo(() => {
    const dinamicas = entradasItemsAnexo(itemsAnexo);
    return construirMenu(MENU_BASE, dinamicas).map(conIcono);
  }, [itemsAnexo]);

  // Filtro por rol / impersonación / empresa dev: src/lib/menu.ts (entradaVisible).
  const navItems = filtrarMenu(menuCompleto, {
    impersonando: !!impersonated,
    esSuperadmin: isSuperAdmin,
    veOcultos: canSeeHiddenModules,
    rol: profile?.role ?? null,
  }) as EntradaConIcono[];

  const SECTION_COLORS = ['#60a5fa', '#fbbf24'];
  const sectionColorMap = new Map<string, string>();
  {
    let colorIdx = 0;
    navItems.forEach(item => {
      if (item.section && !sectionColorMap.has(item.section)) {
        sectionColorMap.set(item.section, SECTION_COLORS[colorIdx % SECTION_COLORS.length]);
        colorIdx++;
      }
    });
  }

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section); else next.add(section);
      const stateObj: Record<string, boolean> = {};
      next.forEach((s) => { stateObj[s] = true; });
      try { localStorage.setItem('ss_sidebar_state', JSON.stringify(stateObj)); } catch {}
      return next;
    });
  };

  const toggleSubsection = (subsection: string) => {
    setExpandedSubsections((prev) => {
      const next = new Set(prev);
      if (next.has(subsection)) next.delete(subsection); else next.add(subsection);
      const stateObj: Record<string, boolean> = {};
      next.forEach((ss) => { stateObj[ss] = true; });
      try { localStorage.setItem('ss_sidebar_subsections', JSON.stringify(stateObj)); } catch {}
      return next;
    });
  };

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group); else next.add(group);
      const stateObj: Record<string, boolean> = {};
      next.forEach(g => { stateObj[g] = true; });
      try { localStorage.setItem('ss_sidebar_groups', JSON.stringify(stateObj)); } catch {}
      return next;
    });
  };

  const handleEnterCompany = (cs: CompanyWithStats) => {
    impersonate({
      id: cs.id, name: cs.name, ruc: cs.ruc, country: cs.country,
      plan: cs.plan, active: cs.active,
      max_warehouses:           cs.max_warehouses,
      max_products:             cs.max_products,
      max_users:                cs.max_users,
      trial_ends_at:            cs.trial_ends_at ?? null,
      emergency_access_enabled: cs.emergency_access_enabled ?? false,
      corporate_domain:         cs.corporate_domain ?? null,
      selected_plan:            cs.selected_plan ?? null,
      subscription_status:      null,
      current_period_end:       null,
      billing_cycle:            cs.billing_cycle ?? null,
      email_notifications:      true,
      is_dev:                   cs.is_dev === true,
    });
    setActive('dashboard');
  };

  const handleExitImpersonation = () => {
    exitImpersonation();
    setActive('superadmin');
  };

  const fetchStats = useCallback(async () => {
    const cid = impersonated?.id ?? null;

    const [
      { count: prodCount },
      { count: whCount },
      { count: userCount },
      { data: zones },
      { data: prods },
      { data: missingSds },
    ] = await Promise.all([
      cid
        ? supabase.from('products').select('*', { count: 'exact', head: true }).eq('company_id', cid)
        : supabase.from('products').select('*', { count: 'exact', head: true }),
      cid
        ? supabase.from('warehouses').select('*', { count: 'exact', head: true }).eq('company_id', cid)
        : supabase.from('warehouses').select('*', { count: 'exact', head: true }),
      cid
        ? supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('active', true).eq('company_id', cid)
        : supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('active', true),
      // warehouse_zones no tiene company_id: se filtra por la empresa de su bodega.
      cid
        ? supabase.from('warehouse_zones').select('id, product_ids, warehouses!inner(company_id)').eq('warehouses.company_id', cid)
        : supabase.from('warehouse_zones').select('id, product_ids'),
      cid
        ? supabase.from('products').select('id, sga_classes').eq('company_id', cid)
        : supabase.from('products').select('id, sga_classes'),
      cid
        ? supabase.from('products').select('id, name').is('sds_url', null).eq('company_id', cid).order('name')
        : supabase.from('products').select('id, name').is('sds_url', null).order('name'),
    ]);

    const classMap = new Map<string, string[]>();
    (prods ?? []).forEach((p: { id: string; sga_classes: string[] }) => classMap.set(p.id, p.sga_classes));

    let alertCount = 0;
    for (const zone of (zones ?? [])) {
      const productClasses = (zone.product_ids as string[]).map((id: string) => classMap.get(id) ?? []);
      if (hasZoneAlert(productClasses)) alertCount++;
    }

    setCounts({
      products:   prodCount  ?? 0,
      warehouses: whCount    ?? 0,
      alerts:     alertCount,
      users:      userCount  ?? 0,
    });
    setPendingSds(missingSds ?? []);
  }, [impersonated]);

  useEffect(() => { if (active === 'dashboard') fetchStats(); }, [active, fetchStats]);

  // Load trial extension request when trial is expired
  useEffect(() => {
    if (!displayCompany?.id || displayCompany.plan !== 'trial') return;
    const trialLeft = displayCompany.trial_ends_at
      ? Math.ceil((new Date(displayCompany.trial_ends_at).getTime() - Date.now()) / 86_400_000)
      : null;
    if (trialLeft === null || trialLeft > 0) { setExtRequest(null); return; }

    supabase
      .from('trial_extension_requests')
      .select('id, status')
      .eq('company_id', displayCompany.id)
      .maybeSingle()
      .then(({ data }) => setExtRequest(data ?? null));
  }, [displayCompany?.id, displayCompany?.plan, displayCompany?.trial_ends_at]);

  const displayName = profile?.full_name || user.email?.split('@')[0] || 'Usuario';

  const handleLogout = async () => { try { sessionStorage.removeItem(CLAVE_SECCION); } catch {} await supabase.auth.signOut(); };

  const handleExtensionRequest = async () => {
    if (!displayCompany?.id || extSubmitting) return;
    setExtSubmitting(true);
    const { data, error } = await supabase
      .from('trial_extension_requests')
      .insert({ company_id: displayCompany.id, user_message: extMessage.trim() || null })
      .select('id, status')
      .single();
    if (!error && data) setExtRequest(data);
    setExtSubmitting(false);
  };

  const handleAcceptTerms = async () => {
    if (!termsChecked || termsAccepting) return;
    setTermsAccepting(true);
    const [profileResult, companyResult] = await Promise.all([
      supabase
        .from('profiles')
        .update({ terms_accepted_at: new Date().toISOString(), terms_accepted_version: TERMS_VERSION })
        .eq('id', user.id),
      company
        ? supabase
            .from('companies')
            .update({ emergency_access_enabled: emergencyConsentChecked })
            .eq('id', company.id)
        : Promise.resolve({ error: null }),
    ]);
    if (profileResult.error) {
      alert('Error al aceptar los términos: ' + profileResult.error.message);
      setTermsAccepting(false);
      return;
    }
    if (companyResult && 'error' in companyResult && companyResult.error) {
      alert('Error al guardar configuración de acceso de emergencia: ' + companyResult.error.message);
      setTermsAccepting(false);
      return;
    }
    refresh();
    setTermsAccepting(false);
  };

  return (
    <div className="db-root">

      {/* ── Welcome banner after successful payment ── */}
      {showWelcome && (
        <div className="db-welcome-toast">
          ¡Bienvenido a StorSafe {PLAN_LABELS[displayCompany?.selected_plan ?? displayCompany?.plan ?? 'starter']}!
          Tu suscripción está activa.
        </div>
      )}

      {/* ── Onboarding wizard (new users only) ── */}
      {profile && !profile.onboarding_completed && !isSuperAdmin && !isTrialExpired && !showTermsModal && (
        <OnboardingModal
          userId={user.id}
          companyId={company?.id ?? ''}
          userName={profile.full_name || user.email?.split('@')[0] || 'Usuario'}
          onComplete={refresh}
          onGoToProducts={() => { refresh(); setActive('productos'); }}
          onGoToWarehouses={() => { refresh(); setActive('bodegas'); }}
          showSSOStep={company?.is_dev === true}
          onGoToJobPositions={() => { refresh(); setActive('puestos-trabajo'); }}
        />
      )}

      {/* ── Trial expired screen (blocking) ── */}
      {isTrialExpired && !isSuperAdmin && (
        <div className="db-trial-overlay">
          <div className="db-trial-expired-card">
            <div className="db-trial-expired-icon">⏰</div>
            <h2 className="db-trial-expired-title">Tu período de prueba ha vencido</h2>
            <p className="db-trial-expired-subtitle">
              Tu trial venció hace{' '}
              <strong>{Math.abs(trialDaysLeft!)} {Math.abs(trialDaysLeft!) === 1 ? 'día' : 'días'}</strong>.
              Para continuar usando StorSafe, activa un plan de pago.
            </p>

            <div className="db-trial-expired-features">
              <div className="db-trial-feat">✓ Inventario ilimitado de productos químicos</div>
              <div className="db-trial-feat">✓ Bodegas y zonas de almacenamiento</div>
              <div className="db-trial-feat">✓ Reportes de cumplimiento normativo</div>
              <div className="db-trial-feat">✓ Soporte técnico prioritario</div>
            </div>

            <div className="db-trial-expired-actions">
              {ppxData ? (
                <PpxButton data={ppxData} />
              ) : (
                <>
                  {/* Plan selector */}
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ color: '#94a3b8', fontSize: '0.78rem', marginBottom: 6, textAlign: 'center' }}>Elige tu plan</p>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {([
                        ['starter',      'Starter',      '$80/mes · $960/año'],
                        ['professional', 'Professional', '$200/mes · $2.400/año'],
                        ['enterprise',   'Enterprise',   '$400/mes · $4.800/año'],
                      ] as const).map(([value, label, price]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setSelectedPlan(value)}
                          style={{
                            padding: '8px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                            border: selectedPlan === value ? '2px solid #2FBF71' : '1px solid #334155',
                            background: selectedPlan === value ? '#1a3a2a' : '#1e293b',
                            color: selectedPlan === value ? '#2FBF71' : '#94a3b8',
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: '0.8rem' }}>{label}</div>
                          <div style={{ fontSize: '0.7rem', marginTop: 2, opacity: 0.85 }}>{price}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Ciclo selector */}
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ color: '#94a3b8', fontSize: '0.78rem', marginBottom: 6, textAlign: 'center' }}>Ciclo de facturación</p>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                      {([
                        ['mensual', 'Mensual', null],
                        ['anual',   'Anual',   '2 meses gratis'],
                      ] as const).map(([value, label, badge]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setSelectedCycle(value)}
                          style={{
                            padding: '8px 20px', borderRadius: 8, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6,
                            border: selectedCycle === value ? '2px solid #2FBF71' : '1px solid #334155',
                            background: selectedCycle === value ? '#1a3a2a' : '#1e293b',
                            color: selectedCycle === value ? '#2FBF71' : '#94a3b8',
                            fontWeight: selectedCycle === value ? 700 : 400, fontSize: '0.82rem',
                          }}
                        >
                          {label}
                          {badge && (
                            <span style={{ background: '#2FBF71', color: '#fff', fontSize: '0.65rem', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>
                              {badge}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    className="db-trial-btn-primary"
                    disabled={ppxLoading}
                    onClick={async () => {
                      if (!company?.id) return;
                      setPpxLoading(true);
                      setPpxError('');
                      try {
                        const breakdown = calculatePrice(selectedPlan, selectedCycle, 0);
                        const data = await buildPagoPluxData(
                          breakdown,
                          user.id,
                          selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1),
                          (response: PagoPluxAuthorizeResponse) => {
                            handlePagoPluxAuthorize(response, company.id, breakdown).catch(console.error);
                          }
                        );
                        setPpxData(data);
                      } catch (err) {
                        setPpxError('No se pudo cargar el formulario de pago');
                      } finally {
                        setPpxLoading(false);
                      }
                    }}
                  >
                    {ppxLoading ? 'Cargando…' : 'Actualizar plan'}
                  </button>
                </>
              )}
              {ppxError && (
                <p style={{ color: '#ef4444', fontSize: '0.82rem', marginTop: 8 }}>{ppxError}</p>
              )}

              <a
                href="https://wa.me/593987591481"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#94a3b8', fontSize: '0.82rem', textDecoration: 'underline' }}
              >
                ¿Necesitas ayuda? Contáctanos
              </a>

              {/* Extension request — one per company */}
              {extRequest === undefined ? null : extRequest === null ? (
                <div className="db-ext-request-box">
                  <p className="db-ext-request-label">¿Necesitas más tiempo? Solicita 5 días adicionales</p>
                  <textarea
                    className="db-ext-request-textarea"
                    placeholder="Cuéntanos por qué necesitas más tiempo (opcional)…"
                    value={extMessage}
                    onChange={(e) => setExtMessage(e.target.value)}
                    disabled={extSubmitting}
                    rows={3}
                  />
                  <button
                    className="db-trial-btn-secondary"
                    onClick={handleExtensionRequest}
                    disabled={extSubmitting}
                  >
                    {extSubmitting ? 'Enviando…' : 'Solicitar 5 días adicionales'}
                  </button>
                </div>
              ) : (
                <div className="db-ext-sent-box">
                  {extRequest.status === 'pending' && (
                    <>
                      <span className="db-ext-sent-icon">📨</span>
                      <span>Solicitud enviada — te contactaremos pronto</span>
                    </>
                  )}
                  {extRequest.status === 'approved' && (
                    <>
                      <span className="db-ext-sent-icon">✅</span>
                      <span>Extensión aprobada. Recarga la página para continuar.</span>
                    </>
                  )}
                  {extRequest.status === 'rejected' && (
                    <>
                      <span className="db-ext-sent-icon">❌</span>
                      <span>Tu solicitud fue rechazada. Contáctanos para más información.</span>
                    </>
                  )}
                </div>
              )}

              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  className="db-trial-btn-secondary"
                  style={{ fontSize: '0.8rem', padding: '8px 12px' }}
                  onClick={() => setShowSupportEmail(v => !v)}
                >
                  Contactar soporte
                </button>
                {showSupportEmail && (
                  <div style={{
                    position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
                    transform: 'translateX(-50%)', background: '#1e293b',
                    border: '1px solid #334155', borderRadius: 8, padding: '12px 16px',
                    whiteSpace: 'nowrap', zIndex: 50, boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                    textAlign: 'center',
                  }}>
                    <p style={{ margin: '0 0 8px', fontSize: '0.82rem', color: '#94a3b8' }}>
                      Escríbenos a: <strong style={{ color: '#e2e8f0' }}>info@storsafe.app</strong>
                    </p>
                    <button
                      style={{
                        background: emailCopied ? '#16a34a' : '#2563eb',
                        color: '#fff', border: 'none', borderRadius: 6,
                        padding: '6px 14px', fontSize: '0.78rem', cursor: 'pointer',
                        fontWeight: 600, transition: 'background 0.2s',
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText('info@storsafe.app');
                        setEmailCopied(true);
                        setTimeout(() => setEmailCopied(false), 2000);
                      }}
                    >
                      {emailCopied ? '¡Copiado!' : 'Copiar correo'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <button className="db-logout-btn" style={{ marginTop: 20 }} onClick={handleLogout}>
              <LogoutIcon />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      )}

      {/* ── Suspended account screen (blocking) ── */}
      {displayCompany?.subscription_status === 'suspended' && !isSuperAdmin && (
        <div className="db-trial-overlay">
          <div className="db-suspended-card">
            <div className="db-suspended-icon">🔒</div>
            <h2 className="db-suspended-title">Cuenta suspendida</h2>
            <p className="db-suspended-subtitle">
              Tu acceso fue suspendido por un pago fallido. Reactiva tu cuenta para continuar usando StorSafe.
            </p>

            <div className="db-suspended-actions">
              <a href="/pricing" className="db-suspended-btn-primary">
                Reactivar cuenta
              </a>

              <a
                href="https://wa.me/593987591481"
                target="_blank"
                rel="noopener noreferrer"
                className="db-trial-btn-secondary"
              >
                Contactar soporte
              </a>

              <button className="db-logout-btn" style={{ marginTop: 10 }} onClick={handleLogout}>
                <LogoutIcon />
                <span>Cerrar sesión</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Terms acceptance modal (first login) ── */}
      {showTermsModal && (
        <div className="db-terms-overlay">
          <div className="db-terms-modal">
            <div className="db-terms-modal-header">
              <TermsModalIcon />
              <div>
                <h2>Términos y Condiciones de Uso</h2>
                <p>Por favor lee y acepta los términos antes de continuar</p>
              </div>
            </div>
            <div className="db-terms-modal-body">
              {showFullTerms ? (
                <>
                  <button
                    className="db-terms-read-link"
                    onClick={() => setShowFullTerms(false)}
                  >
                    ← Volver al resumen
                  </button>
                  <Terms />
                </>
              ) : (
                <>
                  <p>
                    <strong>StorSafe es una herramienta de apoyo.</strong> La información proporcionada por la
                    plataforma (matrices de compatibilidad, planes de emergencia, clasificaciones SGA) es de
                    carácter orientativo y no constituye asesoría profesional certificada en seguridad química.
                  </p>
                  <p>
                    La responsabilidad exclusiva sobre el manejo y almacenamiento de productos químicos recae
                    en el usuario y su organización. StorSafe no asume responsabilidad por daños directos,
                    indirectos, incidentales o lucro cesante derivados del uso de esta plataforma.
                  </p>
                  <p>
                    Al aceptar, confirmas que has leído y entendido que StorSafe no reemplaza la consulta con
                    ingenieros especializados, auditores certificados ni autoridades regulatorias competentes
                    en seguridad industrial y gestión de sustancias peligrosas.
                  </p>
                  <button
                    className="db-terms-read-link"
                    onClick={() => setShowFullTerms(true)}
                  >
                    Leer términos y condiciones completos →
                  </button>
                </>
              )}
            </div>
            <div className="db-terms-modal-footer">
              <label className="db-terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={termsChecked}
                  onChange={e => setTermsChecked(e.target.checked)}
                />
                <span>He leído y acepto los términos y condiciones de uso</span>
              </label>
              <label className="db-terms-checkbox-label db-terms-checkbox-optional">
                <input
                  type="checkbox"
                  checked={emergencyConsentChecked}
                  onChange={e => setEmergencyConsent(e.target.checked)}
                />
                <span>
                  Autorizo el acceso de servicios de emergencia autorizados a la información de mis
                  bodegas con fines de seguridad y emergencias.{' '}
                  <span className="db-terms-optional-tag">Opcional</span>
                </span>
              </label>
              <button
                className="db-terms-accept-btn"
                disabled={!termsChecked || termsAccepting}
                onClick={handleAcceptTerms}
              >
                {termsAccepting ? 'Guardando...' : 'Aceptar y continuar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sidebar ── */}
      {esMovil && menuMovilAbierto && (
        <div className="db-sidebar-backdrop" onClick={() => setMenuMovilAbierto(false)} aria-hidden="true" />
      )}
      <aside
        id="db-menu-lateral"
        className={`db-sidebar ${menuExpandido ? 'open' : 'collapsed'}${esMovil ? ' movil' : ''}${esMovil && menuMovilAbierto ? ' movil-abierto' : ''}`}
        data-tour="tour-sidebar"
        aria-label="Menú principal"
        inert={esMovil && !menuMovilAbierto}
      >
        <div className="db-sidebar-logo">
          <img src={logoUrl} alt="StorSafe" className="db-sidebar-logo-img" />
        </div>

        <nav className="db-nav">
          {navItems.map((item, idx) => {
            const isNewSection = !!item.section && item.section !== navItems[idx - 1]?.section;
            const isNewGroup = !!item.group && (item.group !== navItems[idx - 1]?.group || item.section !== navItems[idx - 1]?.section);
            const isNewSubsection = !!item.subsection && (item.subsection !== navItems[idx - 1]?.subsection || item.group !== navItems[idx - 1]?.group || item.section !== navItems[idx - 1]?.section);
            const sectionExpanded = !item.section || expandedSections.has(item.section);
            const groupExpanded = !item.group || expandedGroups.has(item.group);
            const subsectionExpanded = !item.subsection || expandedSubsections.has(item.subsection);
            const itemVisible = !menuExpandido || sectionExpanded;
            const groupItemVisible = itemVisible && groupExpanded;
            const subsectionItemVisible = groupItemVisible && subsectionExpanded;
            const isLocked = !isSuperAdmin && !!PLAN_FEATURES[item.id] && !hasPlanAccess(displayCompany?.plan, item.id);
            return (
              <React.Fragment key={item.id}>
                {item.separator && idx > 0 && <div className="db-nav-separator" />}
                {isNewSection && menuExpandido && (
                  <button
                    type="button"
                    className="db-nav-section-title"
                    style={{ color: sectionColorMap.get(item.section!) }}
                    onClick={() => toggleSection(item.section!)}
                    data-tour={
                      item.section === 'Gestión Química' ? 'tour-inventario'
                      : item.section === 'Seguridad y Salud' ? 'tour-sso'
                      : undefined
                    }
                  >
                    <span>{item.section}</span>
                    <span className="db-nav-section-arrow">{sectionExpanded ? '▾' : '▸'}</span>
                  </button>
                )}
                {isNewGroup && menuExpandido && itemVisible && (
                  <button
                    type="button"
                    className="db-nav-group-title"
                    onClick={() => toggleGroup(item.group!)}
                  >
                    <span>{item.group}</span>
                    <span className="db-nav-group-arrow">{groupExpanded ? '▾' : '▸'}</span>
                  </button>
                )}
                {isNewSubsection && menuExpandido && groupItemVisible && (
                  <button
                    type="button"
                    className="db-nav-subsection-title"
                    onClick={() => toggleSubsection(item.subsection!)}
                  >
                    <span>{item.subsection}</span>
                    <span className="db-nav-subsection-arrow">{subsectionExpanded ? '▾' : '▸'}</span>
                  </button>
                )}
                {subsectionItemVisible && (
                <button
                  className={`db-nav-item ${active === item.id ? 'active' : ''} ${item.superadminOnly ? 'superadmin' : ''} ${isLocked ? 'locked' : ''} ${item.subsection ? 'with-subsection' : ''} ${item.group ? 'with-group' : ''}`}
                  onClick={() => { if (isLocked) setLockedFeature(item.id); else setActive(item.id); setMenuMovilAbierto(false); }}
                  title={!menuExpandido ? item.label : isLocked ? `Disponible en plan ${PLAN_FEATURES[item.id]?.requiredPlan}` : undefined}
                >
                  <span className="db-nav-icon">{item.icon}</span>
                  {menuExpandido && (
                    <>
                      <span className="db-nav-label" title={item.label}>{item.label}</span>
                      {item.nota && <span className="db-nav-nota">{item.nota}</span>}
                      {item.badge && <span className="db-nav-badge">{item.badge}</span>}
                      {isLocked && <span className="db-nav-lock">🔒</span>}
                    </>
                  )}
                </button>
                )}
              </React.Fragment>
            );
          })}
        </nav>

        {esMovil ? (
          <button className="db-sidebar-toggle" onClick={() => setMenuMovilAbierto(false)} title="Cerrar menú" aria-label="Cerrar menú">
            <ChevronLeftIcon />
          </button>
        ) : (
          <button
            className="db-sidebar-toggle"
            onClick={() => setSidebarOpen((v) => !v)}
            title={sidebarOpen ? 'Colapsar menú' : 'Expandir menú'}
          >
            {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </button>
        )}
      </aside>

      {/* ── Main area ── */}
      <div className="db-main">

        {/* ── Impersonation banner ── */}
        {impersonated && (
          <div className="db-impersonation-bar">
            <EyeSmIcon />
            <span>Vista de empresa: <strong>{impersonated.name}</strong></span>
            <span
              className="db-impersonation-plan"
              style={{ color: PLAN_COLORS[impersonated.plan], borderColor: PLAN_COLORS[impersonated.plan] + '50' }}
            >
              {PLAN_LABELS[impersonated.plan]}
            </span>
            <button className="db-impersonation-exit" onClick={handleExitImpersonation}>
              ← Volver al Super Admin
            </button>
          </div>
        )}

        {/* ── Top bar ── */}
        <header className="db-topbar">
          {esMovil && (
            <button
              type="button"
              className="db-menu-btn"
              onClick={() => setMenuMovilAbierto(true)}
              aria-label="Abrir menú"
              aria-controls="db-menu-lateral"
              aria-expanded={menuMovilAbierto}
            >
              ☰
            </button>
          )}
          <div className="db-topbar-title">
            <h2>
              {navItems.find((n) => n.id === active)?.label ?? ALL_NAV_ITEMS.find((n) => n.id === active)?.label ?? (itemDeNav(active) ? `Ítem ${itemDeNav(active)}` : undefined)}
              {displayCompany && (
                <span className="db-topbar-company">— {displayCompany.name}</span>
              )}
            </h2>
          </div>
          <div className="db-topbar-right">
            {displayCompany && (
              <div
                className="db-plan-badge"
                style={{
                  color:       PLAN_COLORS[displayCompany.plan],
                  borderColor: PLAN_COLORS[displayCompany.plan] + '50',
                  background:  PLAN_COLORS[displayCompany.plan] + '12',
                }}
              >
                {PLAN_LABELS[displayCompany.plan]}
              </div>
            )}
            {trialDaysLeft !== null && !isTrialExpired && !isSuperAdmin && (
              <div className={`db-trial-badge ${isTrialUrgent ? 'urgent' : ''}`}>
                TRIAL · {trialDaysLeft}d
              </div>
            )}
            <div className="db-user-badge" data-tour="tour-profile">
              <div className="db-avatar">{displayName[0].toUpperCase()}</div>
              <div className="db-user-info">
                <span className="db-user-name">{displayName}</span>
                {profile ? (
                  <span
                    className="db-user-role"
                    style={{ color: ROLE_COLORS[profile.role]?.color }}
                  >
                    {ROLE_LABELS[profile.role]}
                  </span>
                ) : (
                  <span className="db-user-email">{user.email}</span>
                )}
              </div>
            </div>
            <button className="db-logout-btn" onClick={handleLogout} title="Cerrar sesión">
              <LogoutIcon />
              <span>Salir</span>
            </button>
          </div>
        </header>

        {/* ── Trial banner (dismissible, resets on reload) ── */}
        {showTrialBanner && (
          <div className={`db-trial-banner ${trialBannerVariant}`}>
            <span className="db-trial-banner-text">{trialBannerText}</span>
            <div className="db-trial-banner-actions">
              <a href="/pricing" className="db-trial-banner-cta">Ver planes</a>
              <button
                className="db-trial-banner-close"
                onClick={() => setTrialBannerDismissed(true)}
                aria-label="Cerrar banner"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* ── Onboarding checklist (primeros pasos) ── */}
        {profile?.onboarding_completed && !isSuperAdmin && (
          <div data-tour="tour-checklist">
            <OnboardingChecklist
              companyId={displayCompany?.id ?? ''}
              onNavigate={(tab) => setActive(tab as NavItem)}
            />
          </div>
        )}

        {/* ── Content ── */}
        <main className="db-content">
          {active === 'dashboard' && (
            <>
              <div className="db-welcome">
                <h3>Bienvenido, <span>{displayName}</span></h3>
                <p>Resumen del sistema · {new Date().toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>

              <div className="db-stats-grid">
                {STAT_TEMPLATES.map((stat) => {
                  const count = counts[stat.key];
                  const limit = stat.limitKey ? (displayCompany?.[stat.limitKey] as number | null | undefined) ?? null : null;
                  const percent = limit ? Math.min(100, Math.round((count / limit) * 100)) : null;
                  const barColor = percent !== null
                    ? percent > 90 ? '#ef4444'
                    : percent > 70 ? '#f59e0b'
                    : stat.color
                    : stat.color;

                  return (
                    <div
                      className="db-stat-card db-stat-card--clickable"
                      key={stat.label}
                      onClick={() => setActive(stat.tab)}
                    >
                      <div className="db-stat-icon" style={{ background: stat.bg, color: stat.color }}>
                        {stat.icon}
                      </div>
                      <div className="db-stat-body">
                        <div className="db-stat-value-row">
                          <span className="db-stat-value" style={{ color: barColor }}>{count}</span>
                          {limit !== null && (
                            <span className="db-stat-limit">/{limit}</span>
                          )}
                        </div>
                        {percent !== null && (
                          <div className="db-stat-bar">
                            <div
                              className="db-stat-bar-fill"
                              style={{ width: `${percent}%`, background: barColor }}
                            />
                          </div>
                        )}
                        <span className="db-stat-label">{stat.label}</span>
                      </div>
                    </div>
                  );
                })}

                {canSeeHiddenModules && (
                  <div
                    className="db-stat-card"
                    title={complianceScore.breakdown
                      .map((c) => `${c.label}: ${c.applicable ? Math.round(c.pct) + '%' : 'no aplica'}`)
                      .join('\n')}
                  >
                    <div className="db-stat-icon" style={{ background: complianceScore.color + '1a', color: complianceScore.color }}>
                      <ShieldCheckIcon />
                    </div>
                    <div className="db-stat-body">
                      <div className="db-stat-value-row">
                        <span className="db-stat-value" style={{ color: complianceScore.color }}>
                          {complianceScore.loading ? '—' : complianceScore.score}
                        </span>
                        <span className="db-stat-limit">/100</span>
                      </div>
                      <div className="db-stat-bar">
                        <div
                          className="db-stat-bar-fill"
                          style={{ width: `${complianceScore.loading ? 0 : complianceScore.score}%`, background: complianceScore.color }}
                        />
                      </div>
                      <span className="db-stat-label">Score de Cumplimiento SSO</span>
                    </div>
                  </div>
                )}
              </div>

              {/* ── SDS Pending Widget ── */}
              {pendingSds.length > 0 && (
                <div className="db-sds-widget">
                  <button
                    className="db-sds-header"
                    onClick={() => setSdsPanelOpen((v) => !v)}
                    aria-expanded={sdsPanelOpen}
                  >
                    <div className="db-sds-icon-wrap">
                      <SdsWarningIcon />
                    </div>
                    <div className="db-sds-title-area">
                      <span className="db-sds-count">{pendingSds.length}</span>
                      <span className="db-sds-label">
                        Hoja{pendingSds.length !== 1 ? 's' : ''} de Seguridad Pendiente{pendingSds.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <span className={`db-sds-chevron ${sdsPanelOpen ? 'open' : ''}`}>
                      <ChevronRightIcon />
                    </span>
                  </button>

                  {sdsPanelOpen && (
                    <div className="db-sds-body">
                      <p className="db-sds-hint">
                        Los siguientes productos no tienen hoja de seguridad cargada:
                      </p>
                      <ul className="db-sds-list">
                        {pendingSds.slice(0, 6).map((p) => (
                          <li key={p.id} className="db-sds-item">
                            <span className="db-sds-item-dot" />
                            <span className="db-sds-item-name">{p.name}</span>
                            <button
                              className="db-sds-item-btn"
                              onClick={() => setActive('productos')}
                              title="Ir a Productos para subir el PDF"
                            >
                              Subir PDF
                            </button>
                          </li>
                        ))}
                        {pendingSds.length > 6 && (
                          <li className="db-sds-more">
                            y {pendingSds.length - 6} producto{pendingSds.length - 6 !== 1 ? 's' : ''} más…
                          </li>
                        )}
                      </ul>
                      <button className="db-sds-goto" onClick={() => setActive('productos')}>
                        Ir a Productos →
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="db-empty-state">
                <div className="db-empty-icon"><EmptyIcon /></div>
                <p className="db-empty-title">Sistema listo</p>
                <p className="db-empty-sub">Usa el menú lateral para comenzar a agregar productos y bodegas.</p>
              </div>
            </>
          )}

          {active === 'productos'      && <Products />}
          {active === 'compatibilidad' && <CompatibilityMatrix />}
          {active === 'bodegas'        && <Warehouses />}
          {active === 'usuarios'       && <Users />}
          {active === 'reportes'       && <Reports />}
          {active === 'emergencias'    && <Emergency />}
          {active === 'auditoria'      && <Audit />}
          {active === 'layout'         && <LayoutPro />}
          {active === 'dashboard-sso'  && canSeeHiddenModules && <SSODashboardPage />}
          {active === 'calendario-sso' && canSeeHiddenModules && <SSOCalendarPage />}
          {active === 'sso-perfil'     && canSeeHiddenModules && <SSOProfilePage companyId={displayCompany?.id ?? ''} onEdit={() => {}} onNavigateToSedes={() => setActive('sedes')} />}
          {active === 'sedes'          && canSeeHiddenModules && <SedesPage onNavigateToSSOPerfil={() => setActive('sso-perfil')} />}
          {active === 'checklist'      && canSeeChecklist && (
            <ChecklistPage
              onNavigateToDocumentosSSO={() => setActive('documentos-sso')}
              onNavigateToSSOPerfil={() => setActive('sso-perfil')}
              onNavigateToVigilanciaSalud={() => setActive('vigilancia-salud')}
              onNavigateToPermisos={() => setActive('permisos-trabajo')}
              onNavigateToProductos={() => setActive('productos')}
              onNavigateToEtiquetadoSGA={() => setActive('etiquetado-sga')}
              onNavigateToInspecciones={() => setActive('inspecciones')}
              onNavigateToInspeccionesV2={() => setActive('inspecciones-v2')}
              onNavigateToPuestos={() => setActive('puestos-trabajo')}
              onNavigateToMatrizRiesgos={() => setActive('iper')}
              onNavigateToTrabajadores={() => setActive('trabajadores')}
              onNavigateToCapacitaciones={() => setActive('capacitaciones')}
              onNavigateToIncidentes={() => setActive('incidentes')}
              onNavigateToPlanEmergencias={() => setActive('plan-emergencias')}
              onNavigateToEPP={() => setActive('epp')}
              onNavigateToRiesgoPsicosocial={() => setActive('riesgo-psicosocial')}
              onNavigateToConstructorGT={() => setActive('constructor-gestion-tecnica')}
              onNavigateToItemAnexo={(itemId) => setActive(idItemAnexo(itemId))}
            />
          )}
          {active === 'trabajadores'   && canSeeHiddenModules && <WorkersPage onNavigateToSSOPerfil={() => setActive('sso-perfil')} />}
          {active === 'puestos-trabajo' && canSeeHiddenModules && <JobPositionsPage />}
          {active === 'constructor-gestion-tecnica' && canSeeHiddenModules && <GestionTecnicaConstructorPage />}
          {active === 'metodologia-coshh' && <CoshhMethodologyPage />}
          {active === 'metodologia-biogaval' && canSeeHiddenModules && <BiogavalMethodologyPage />}
          {active === 'higiene-fisico' && canSeeHiddenModules && <div style={{padding: 24}}>Higiene y Gestión del Riesgo Físico — en construcción</div>}
          {active === 'metodologia-iluminacion' && canSeeHiddenModules && <IluminacionMethodologyPage />}
          {active === 'metodologia-ruido' && canSeeHiddenModules && <RuidoMethodologyPage />}
          {active === 'metodologia-estres-termico' && canSeeHiddenModules && <EstresTermicoMethodologyPage />}
          {active === 'metodologia-rula' && canSeeHiddenModules && <RulaMethodologyPage />}
          {active === 'metodologia-reba' && canSeeHiddenModules && <RebaMethodologyPage />}
          {active === 'ergonomia-manipulacion-cargas' && canSeeHiddenModules && <NioshMethodologyPage />}
          {active === 'metodologia-snook-ciriello' && canSeeHiddenModules && <SnookCirielloMethodologyPage />}
          {active === 'ergonomia-repetitividad' && canSeeHiddenModules && <div style={{padding: 24}}>Repetitividad — en construcción</div>}
          {active === 'ergonomia-puestos-oficina' && canSeeHiddenModules && <RosaMethodologyPage />}
          {active === 'higiene-biologico' && canSeeHiddenModules && <div style={{padding: 24}}>Higiene y Gestión del Riesgo Biológico — en construcción</div>}
          {active === 'epp'            && canSeeHiddenModules && <EPPPage />}
          {active === 'incidentes'     && canSeeHiddenModules && <IncidentsPage />}
          {active === 'documentos-sso' && canSeeHiddenModules && <SSODocumentsPage />}
          {itemDeNav(active) && canSeeHiddenModules && (
            <ItemAnexoPage
              key={active}
              itemId={itemDeNav(active) as string}
              onNavigate={(tab) => setActive(tab)}
            />
          )}
          {active === 'capacitaciones'  && canSeeHiddenModules && <TrainingsPage />}
          {active === 'learning'        && canSeeHiddenModules && <LearningAdminPage />}
          {active === 'etiquetado-sga'  && <SGALabelsPage />}
          {active === 'inspecciones'    && canSeeHiddenModules && <InspectionsPage />}
          {active === 'inspecciones-v2' && canSeeHiddenModules && <InspeccionesV2 />}
          {active === 'inventario-activos' && canSeeHiddenModules && <InventarioActivos />}
          {active === 'iper'            && canSeeHiddenModules && <IperPage />}
          {active === 'vigilancia-salud' && canSeeHiddenModules && <HealthSurveillancePage onNavigateToWorkers={() => setActive('trabajadores')} />}
          {active === 'permisos-trabajo' && canSeeHiddenModules && <WorkPermitsPage />}
          {active === 'plan-emergencias' && canSeeHiddenModules && <EmergencyPlanPage />}
          {active === 'plan-emergencias-v2' && canSeeHiddenModules && <PlanEmergenciasV2 />}
          {active === 'riesgo-psicosocial' && canSeeHiddenModules && <PsychosocialRiskPage />}
          {active === 'cuestionario-psicosocial' && canSeeHiddenModules && <PsicosocialCodigosPage />}
          {active === 'resultados-psicosocial' && canSeePsicosocialResultados &&<PsicosocialResultadosPage />}
          {active === 'configuracion'  && <Settings />}
          {active === 'terminos'       && <Terms onBack={() => setActive('dashboard')} />}
          {active === 'superadmin'     && isSuperAdmin && <SuperAdmin onEnterCompany={handleEnterCompany} />}
        </main>
      </div>

      {/* ── Tour guiado (primer ingreso post-onboarding) ── */}
      {profile?.onboarding_completed && !isSuperAdmin && !showTermsModal && <OnboardingTour />}

      {/* ── Upgrade modal contextual (candado del sidebar) ── */}
      {lockedFeature && PLAN_FEATURES[lockedFeature] && (
        <div className="db-lock-modal-overlay" onClick={() => setLockedFeature(null)}>
          <div className="db-lock-modal-box" onClick={(e) => e.stopPropagation()}>
            <button className="db-lock-modal-close" onClick={() => setLockedFeature(null)} aria-label="Cerrar">✕</button>
            <UpgradeWall {...PLAN_FEATURES[lockedFeature]} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── SVG Icons ────────────────────────────────────────────────────────────────

function BoxIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}

function WarehouseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function ShieldCheckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function NavDashboardIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function NavBoxIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}

function NavGridIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" />
      <line x1="15" y1="3" x2="15" y2="21" />
    </svg>
  );
}

function NavWarehouseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function NavUsersIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function NavShieldIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function NavSSOIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <line x1="12" y1="10" x2="12" y2="14" />
      <circle cx="12" cy="16" r="1" fill="currentColor" />
    </svg>
  );
}

function NavBriefcaseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
      <line x1="12" y1="12" x2="12" y2="12.01" />
      <path d="M2 12h20" />
    </svg>
  );
}

function NavWorkersIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
      <line x1="19" y1="8" x2="23" y2="8" />
      <line x1="21" y1="6" x2="21" y2="10" />
    </svg>
  );
}

function NavIncidentsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function NavReportIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function NavAlertIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function NavAuditIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" />
      <circle cx="18" cy="18" r="4" /><line x1="20.5" y1="20.5" x2="22" y2="22" />
    </svg>
  );
}

function NavLayoutIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function EmptyIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}

function EyeSmIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function SdsWarningIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="12" y1="12" x2="12" y2="16" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  );
}

function NavSettingsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}

function NavTermsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="9" x2="15" y2="9" />
    </svg>
  );
}

function NavChartBarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6"  y1="20" x2="6"  y2="14" />
      <line x1="2"  y1="20" x2="22" y2="20" />
    </svg>
  );
}

function NavCalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8"  y1="2" x2="8"  y2="6" />
      <line x1="3"  y1="10" x2="21" y2="10" />
    </svg>
  );
}

function NavDocsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
    </svg>
  );
}

function NavTrainingIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  );
}

function NavLabelIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  );
}

function NavInspectIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <polyline points="9 13 11 15 15 11" />
    </svg>
  );
}

function NavShieldCheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function NavIperIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <line x1="8" y1="9"  x2="16" y2="9" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="8" y1="15" x2="12" y2="15" />
    </svg>
  );
}

function NavHealthIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 10-7.78 7.78l1.06 1.06L12 21l7.78-7.55 1.06-1.06a5.5 5.5 0 000-7.78z" />
      <line x1="12" y1="9" x2="12" y2="14" />
      <line x1="9.5" y1="11.5" x2="14.5" y2="11.5" />
    </svg>
  );
}

function NavPermitIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <polyline points="8 14 10 16 16 10" />
    </svg>
  );
}

function TermsModalIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}
